import { atom } from 'jotai';
import { fetchApi } from '../../lib/apiClient';
import { ReminderMethodType } from '../../types/notificationMapping';

// 定时对话配置接口
export interface ScheduledConversation {
  id: string;
  userId: string;
  templateKey: string;
  name: string;
  enabled: boolean;
  isSystemInstance: boolean;
  channels?: ReminderMethodType[]; // 推送渠道配置
  createdAt: string | null;
  updatedAt: string | null;
}

// API 响应接口
interface ScheduledConversationsResponse {
  success: boolean;
  data: ScheduledConversation[];
}

interface UpdateScheduledConversationResponse {
  success: boolean;
  message: string;
}

interface TriggerScheduledConversationResponse {
  success: boolean;
  message: string;
}

/**
 * 定时对话配置列表原子
 */
export const scheduledConversationsAtom = atom<ScheduledConversation[]>([]);

/**
 * 定时对话配置加载状态原子
 */
export const scheduledConversationsLoadingAtom = atom<boolean>(false);

/**
 * 定时对话配置错误状态原子
 */
export const scheduledConversationsErrorAtom = atom<string | null>(null);

/**
 * 获取定时对话配置的原子
 */
export const fetchScheduledConversationsAtom = atom(
  null,
  async (get, set) => {
    set(scheduledConversationsLoadingAtom, true);
    set(scheduledConversationsErrorAtom, null);

    try {
      const response = await fetchApi<ScheduledConversationsResponse>('/scheduled-conversations');

      if (!response.success) {
        throw new Error('获取定时对话配置失败');
      }

      set(scheduledConversationsAtom, response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '获取定时对话配置失败';
      set(scheduledConversationsErrorAtom, errorMessage);
      console.error('获取定时对话配置失败:', error);
      throw error;
    } finally {
      set(scheduledConversationsLoadingAtom, false);
    }
  }
);

/**
 * 更新定时对话配置的原子
 */
export const updateScheduledConversationAtom = atom(
  null,
  async (get, set, { id, enabled, channels }: { id: string; enabled?: boolean; channels?: ReminderMethodType[] }) => {
    try {
      // 构建更新数据
      const updateData: any = {};
      if (enabled !== undefined) updateData.enabled = enabled;
      if (channels !== undefined) updateData.channels = channels;

      // 发送更新请求
      const response = await fetchApi<UpdateScheduledConversationResponse>(
        `/scheduled-conversations/${id}`,
        {
          method: 'PUT',
          body: updateData
        }
      );

      if (!response.success) {
        throw new Error(response.message || '更新定时对话配置失败');
      }

      // 更新本地状态
      const currentConversations = get(scheduledConversationsAtom);
      const updatedConversations = currentConversations.map(conv =>
        conv.id === id ? { ...conv, ...updateData, updatedAt: new Date().toISOString() } : conv
      );
      set(scheduledConversationsAtom, updatedConversations);

      return { success: true, message: '定时对话配置更新成功' };
    } catch (error) {
      console.error('更新定时对话配置失败:', error);
      throw error;
    }
  }
);

/**
 * 手动触发定时对话的原子
 */
export const triggerScheduledConversationAtom = atom(
  null,
  async (get, set, id: string) => {
    try {
      const response = await fetchApi<TriggerScheduledConversationResponse>(
        `/scheduled-conversations/${id}/trigger`,
        {
          method: 'POST'
        }
      );

      if (!response.success) {
        throw new Error(response.message || '触发定时对话失败');
      }

      return { success: true, message: response.message };
    } catch (error) {
      console.error('触发定时对话失败:', error);
      throw error;
    }
  }
);

/**
 * 根据模板键获取定时对话配置的原子
 */
export const getScheduledConversationByTemplateKeyAtom = atom(
  (get) => (templateKey: string): ScheduledConversation | undefined => {
    const conversations = get(scheduledConversationsAtom);
    return conversations.find(conv => conv.templateKey === templateKey);
  }
);
